import React, { useEffect, useState } from 'react';
import { authHeaders } from '../../services/auth';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import {
  ScrollText, Save, Loader2, Plus, Trash2, ArrowUp, ArrowDown,
  AlertCircle, Star, Eye, EyeOff
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const slugify = (s) =>
  (s || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || `section-${Date.now().toString(36)}`;

export const SATCDocumentEditor = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [version, setVersion] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [sections, setSections] = useState([]);
  const [bumpVersion, setBumpVersion] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  const [collapsedAll, setCollapsedAll] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/tc/super-admin/document`, {
        headers: authHeaders(), credentials: 'include',
      });
      if (res.ok) {
        const j = await res.json();
        setVersion(j.version || '');
        setEffectiveDate(j.effective_date || '');
        setSections((j.sections || []).map((s) => ({
          ...s,
          body: Array.isArray(s.body) ? s.body : [],
        })));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!version.trim() || !effectiveDate.trim()) {
      toast.error('Version & effective date are required'); return;
    }
    if (sections.length === 0) {
      toast.error('At least one section is required'); return;
    }
    const seen = new Set();
    for (const s of sections) {
      if (!s.id || !s.title.trim()) { toast.error(`Section "${s.title || s.id}" is missing id or title`); return; }
      if (seen.has(s.id)) { toast.error(`Duplicate section id: ${s.id}`); return; }
      seen.add(s.id);
    }
    setSaving(true);
    try {
      const payload = {
        version: version.trim(),
        effective_date: effectiveDate.trim(),
        bump_version: bumpVersion,
        sections: sections.map((s) => ({
          id: s.id,
          title: s.title.trim(),
          body: (s.body || []).map((p) => p ?? '').filter((p) => p !== ''),
          highlight: !!s.highlight,
        })),
      };
      const res = await fetch(`${API}/api/tc/super-admin/document`, {
        method: 'PUT',
        credentials: 'include',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(j.detail || 'Save failed'); return; }
      toast.success(j.version_bumped
        ? `T&C published as new version v${j.version} — all users will be re-prompted on next login.`
        : `T&C saved (v${j.version})`
      );
      setBumpVersion(false);
      await load();
    } finally {
      setSaving(false);
    }
  };

  const updateSection = (idx, patch) => {
    setSections((prev) => prev.map((s, i) => i === idx ? { ...s, ...patch } : s));
  };

  const moveSection = (idx, dir) => {
    setSections((prev) => {
      const next = [...prev];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };

  const deleteSection = (idx) => {
    if (!window.confirm(`Delete section "${sections[idx]?.title}"?`)) return;
    setSections((prev) => prev.filter((_, i) => i !== idx));
  };

  const addSection = () => {
    const newId = `section-${sections.length + 1}-${Date.now().toString(36).slice(-4)}`;
    setSections((prev) => [...prev, { id: newId, title: 'New section', body: [''], highlight: false }]);
    setCollapsed((c) => ({ ...c, [newId]: false }));
  };

  const updateBody = (idx, text) => {
    // user types one paragraph per line; we store as array
    const lines = text.split(/\n{2,}|\r\n\r\n/).map((p) => p.trim()).filter(Boolean);
    updateSection(idx, { body: lines.length ? lines : [''] });
  };

  const toggleCollapseAll = () => {
    if (collapsedAll) {
      setCollapsed({});
      setCollapsedAll(false);
    } else {
      const all = {};
      sections.forEach((s) => { all[s.id] = true; });
      setCollapsed(all);
      setCollapsedAll(true);
    }
  };

  if (loading) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-6 mt-6" data-testid="sa-tc-editor-loading">
        <div className="text-sm text-slate-500">Loading T&C editor…</div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 mt-6" data-testid="sa-tc-document-editor">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ScrollText className="w-5 h-5 text-indigo-700" />
          <h3 className="text-lg font-semibold text-slate-900">Terms & Conditions Editor</h3>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={toggleCollapseAll} data-testid="sa-tc-collapse-all">
            {collapsedAll ? <><Eye className="w-4 h-4 mr-1" /> Expand all</> : <><EyeOff className="w-4 h-4 mr-1" /> Collapse all</>}
          </Button>
        </div>
      </div>

      <p className="text-xs text-slate-500 mb-4">
        Edit any section. Changes save in place. Tick <strong>Bump version</strong> below to publish as a new version, which forces all users to re-accept on next login.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        <div>
          <Label className="text-xs">Version</Label>
          <Input
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            data-testid="sa-tc-version-input"
            disabled={bumpVersion}
            placeholder="e.g. 1.0.1"
          />
          {bumpVersion && (
            <p className="text-[11px] text-slate-500 mt-1">Will be auto-bumped on save.</p>
          )}
        </div>
        <div>
          <Label className="text-xs">Effective date</Label>
          <Input
            value={effectiveDate}
            onChange={(e) => setEffectiveDate(e.target.value)}
            data-testid="sa-tc-effective-date-input"
            placeholder="e.g. February 2026"
          />
        </div>
        <div className="flex items-end">
          <label className="flex items-start gap-2 cursor-pointer text-sm">
            <Checkbox
              checked={bumpVersion}
              onCheckedChange={(v) => setBumpVersion(!!v)}
              data-testid="sa-tc-bump-version"
              className="mt-0.5"
            />
            <span className="text-slate-800">
              Bump version on save<br />
              <span className="text-[11px] text-amber-700">(forces re-acceptance for all users)</span>
            </span>
          </label>
        </div>
      </div>

      <div className="space-y-3 mb-4" data-testid="sa-tc-sections-list">
        {sections.map((s, idx) => {
          const isCollapsed = !!collapsed[s.id];
          return (
            <div
              key={s.id}
              className={`border rounded-lg ${s.highlight ? 'border-amber-300 bg-amber-50/40' : 'border-slate-200 bg-slate-50/30'}`}
              data-testid={`sa-tc-section-${s.id}`}
            >
              <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-200 bg-white">
                <Button size="sm" variant="ghost" onClick={() => setCollapsed((c) => ({ ...c, [s.id]: !c[s.id] }))} className="h-8 px-2">
                  {isCollapsed ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                </Button>
                <span className="text-xs text-slate-500 font-mono">#{idx + 1}</span>
                {s.highlight && <Star className="w-3.5 h-3.5 text-amber-600" />}
                <span className="text-sm font-medium text-slate-800 truncate flex-1">{s.title}</span>
                <Button size="sm" variant="ghost" onClick={() => moveSection(idx, -1)} disabled={idx === 0} className="h-8 px-2">
                  <ArrowUp className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => moveSection(idx, 1)} disabled={idx === sections.length - 1} className="h-8 px-2">
                  <ArrowDown className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => deleteSection(idx)} className="h-8 px-2 text-red-600 hover:text-red-700 hover:bg-red-50">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              {!isCollapsed && (
                <div className="p-3 space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="md:col-span-2">
                      <Label className="text-xs">Title</Label>
                      <Input
                        value={s.title}
                        onChange={(e) => updateSection(idx, { title: e.target.value })}
                        data-testid={`sa-tc-section-title-${s.id}`}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Section ID</Label>
                      <div className="flex gap-1">
                        <Input
                          value={s.id}
                          onChange={(e) => {
                            const old = s.id;
                            const next = slugify(e.target.value);
                            updateSection(idx, { id: next });
                            setCollapsed((c) => {
                              const cn = { ...c };
                              if (old in cn) { cn[next] = cn[old]; delete cn[old]; }
                              return cn;
                            });
                          }}
                          data-testid={`sa-tc-section-id-${s.id}`}
                          className="font-mono text-xs"
                        />
                      </div>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Body (separate paragraphs with one blank line)</Label>
                    <Textarea
                      value={(s.body || []).join('\n\n')}
                      onChange={(e) => updateBody(idx, e.target.value)}
                      data-testid={`sa-tc-section-body-${s.id}`}
                      rows={Math.min(12, Math.max(4, (s.body || []).join('\n\n').split('\n').length + 1))}
                      className="font-mono text-xs leading-relaxed"
                    />
                    <p className="text-[11px] text-slate-500 mt-1">
                      Special token: <code className="bg-slate-100 px-1 rounded">__GRIEVANCE_CONTACT_BLOCK__</code>
                      {' '}as a paragraph renders the live grievance contact block.
                    </p>
                  </div>
                  <label className="flex items-center gap-2 text-xs cursor-pointer">
                    <Checkbox
                      checked={!!s.highlight}
                      onCheckedChange={(v) => updateSection(idx, { highlight: !!v })}
                      data-testid={`sa-tc-section-highlight-${s.id}`}
                    />
                    <span className="text-slate-700">Highlight (yellow callout — use for the RFID consent or any critical clause)</span>
                  </label>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between gap-2 mb-2">
        <Button variant="outline" onClick={addSection} data-testid="sa-tc-add-section">
          <Plus className="w-4 h-4 mr-1" /> Add section
        </Button>
      </div>

      <div className="mt-4 pt-4 border-t border-slate-200 flex items-center justify-between gap-3">
        <div className="text-xs text-slate-500 flex items-center gap-1.5">
          <AlertCircle className="w-3.5 h-3.5 text-slate-400" />
          {bumpVersion
            ? <span className="text-amber-700 font-medium">Bumping version will force every user to re-accept the T&C on their next login.</span>
            : 'Saving without bump simply updates the live document in place.'}
        </div>
        <Button onClick={save} disabled={saving} data-testid="sa-tc-save-button" className="bg-indigo-600 hover:bg-indigo-700 text-white">
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
          Save
        </Button>
      </div>
    </div>
  );
};

export default SATCDocumentEditor;
